import { endOfMonth, format } from 'date-fns';
import supabase from '@/api/supabase';

export interface TaskIncomeRow {
  income?: number;
  checkinTime: string;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const getDateParts = (checkinTime: string) => {
  const [year, month, day] = checkinTime.split('T')[0].split('-').map(Number);
  return { year, monthIndex: month - 1, day };
};

export const getMonthDateRange = (year: number, monthIndex: number) => {
  const start = format(new Date(year, monthIndex, 1), 'yyyy-MM-dd');
  const end = format(endOfMonth(new Date(year, monthIndex, 1)), 'yyyy-MM-dd');
  return { start, end };
};

export const getYearDateRange = (year: number) => ({
  start: `${year}-01-01`,
  end: `${year}-12-31`,
});

export const getComparablePeriodEnd = (selectedYear: number) => {
  const now = new Date();
  if (selectedYear < now.getFullYear()) {
    return `${selectedYear}-12-31`;
  }
  if (selectedYear > now.getFullYear()) {
    return `${selectedYear}-01-01`;
  }
  return format(now, 'yyyy-MM-dd');
};

export const isOnOrBefore = (checkinTime: string, cutoffDate: string) =>
  checkinTime.split('T')[0] <= cutoffDate;

export async function fetchTaskHistoryRange(
  start: string,
  end: string,
  select = 'income, checkinTime'
): Promise<TaskIncomeRow[]> {
  const pageSize = 1000;
  let from = 0;
  const allRows: TaskIncomeRow[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('taskManager_history')
      .select(select)
      .gte('checkinTime', start)
      .lte('checkinTime', end)
      .order('checkinTime', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) throw error;
    if (!data?.length) break;

    allRows.push(...(data as unknown as TaskIncomeRow[]));
    hasMore = data.length === pageSize;
    from += pageSize;
  }

  return allRows;
}

export const aggregateMonthlyIncome = (rows: TaskIncomeRow[], year: number) =>
  rows.reduce<Record<number, number>>((totals, item) => {
    const { year: rowYear, monthIndex } = getDateParts(item.checkinTime);
    if (rowYear !== year || monthIndex < 0 || monthIndex > 11) {
      return totals;
    }

    totals[monthIndex] = (totals[monthIndex] || 0) + (item.income || 0);
    return totals;
  }, {});

export const sumIncomeUpToDate = (rows: TaskIncomeRow[], year: number, cutoffDate: string) =>
  rows.reduce((sum, item) => {
    const { year: rowYear } = getDateParts(item.checkinTime);
    if (rowYear !== year || !isOnOrBefore(item.checkinTime, cutoffDate)) {
      return sum;
    }
    return sum + (item.income || 0);
  }, 0);

const getMondayWeekStart = (dateString: string) => {
  const date = new Date(`${dateString}T00:00:00`);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
};

export const formatWeekRange = (weekStart: Date) => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return `${MONTH_LABELS[weekStart.getMonth()]} ${weekStart.getDate()} - ${MONTH_LABELS[weekEnd.getMonth()]} ${weekEnd.getDate()}`;
};

export interface WeeklyIncomeData {
  weekRange: string;
  total: number;
  percentage: number;
}

export const aggregateWeeklyIncomeForMonth = (
  rows: TaskIncomeRow[],
  year: number,
  monthIndex: number
): WeeklyIncomeData[] => {
  const weekTotals = new Map<string, number>();

  rows.forEach(item => {
    const dateString = item.checkinTime.split('T')[0];
    const { year: rowYear, monthIndex: rowMonthIndex } = getDateParts(item.checkinTime);

    if (rowYear !== year || rowMonthIndex !== monthIndex) {
      return;
    }

    const weekStart = getMondayWeekStart(dateString);
    const weekKey = format(weekStart, 'yyyy-MM-dd');
    weekTotals.set(weekKey, (weekTotals.get(weekKey) || 0) + (item.income || 0));
  });

  const weeklyData = Array.from(weekTotals.entries())
    .map(([weekKey, total]) => ({
      weekRange: formatWeekRange(new Date(`${weekKey}T00:00:00`)),
      total,
      percentage: 0,
    }))
    .filter(week => week.total > 0);

  const monthTotal = weeklyData.reduce((sum, week) => sum + week.total, 0);
  weeklyData.forEach(week => {
    week.percentage = monthTotal ? (week.total / monthTotal) * 100 : 0;
  });

  return weeklyData.sort((a, b) => b.total - a.total);
};

export const getTopMonth = (
  monthlyTotals: Record<number, number>,
  selectedYear: number
) => {
  const now = new Date();
  const lastMonthIndex = selectedYear < now.getFullYear() ? 11 : now.getMonth();

  let topMonthIndex = 0;
  let topIncome = 0;

  for (let month = 0; month <= lastMonthIndex; month += 1) {
    const income = monthlyTotals[month] || 0;
    if (income > topIncome) {
      topIncome = income;
      topMonthIndex = month;
    }
  }

  return {
    month: new Date(2000, topMonthIndex).toLocaleString('default', { month: 'short' }),
    income: topIncome,
  };
};
